import React, { useEffect, useCallback } from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useDashboard } from '../../../contexts/DashboardContext';
import { ChartCard, ChartGrid, CustomTooltip, CHART_COLORS, formatCurrency, DonutChartWithLabels } from '../ChartComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PSLRegulatoryTab = () => {
  const { dataCache, setTabData, loading, setTabLoading, errors, setTabError, filters, isTabCached } = useDashboard();
  
  const data = dataCache.psl;

  const fetchData = useCallback(async () => {
    if (isTabCached('psl')) return;
    
    setTabLoading('psl', true);
    setTabError('psl', null);
    
    try {
      const params = new URLSearchParams();
      if (filters.asOnDate) params.append('as_on_date', filters.asOnDate);
      if (filters.branch) params.append('branch', filters.branch);
      
      const response = await fetch(`${API_URL}/api/dashboard/psl-regulatory?${params}`);
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const result = await response.json();
      setTabData('psl', result);
    } catch (err) {
      setTabError('psl', err.message);
    } finally {
      setTabLoading('psl', false);
    }
  }, [filters, isTabCached, setTabData, setTabLoading, setTabError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading.psl) {
    return (
      <ChartGrid>
        {[1, 2, 3, 4].map(i => <ChartCard key={i} title="" loading={true} />)}
      </ChartGrid>
    );
  }

  if (errors.psl) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">Error loading data: {errors.psl}</p>
        <button onClick={fetchData} className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { charts } = data;

  return (
    <ChartGrid>
      {/* Chart 1: PSL vs Non-PSL Distribution (Donut) */}
      <ChartCard title="PSL vs Non-PSL Distribution" subtitle="Priority sector lending split">
        <div className="h-64">
          <DonutChartWithLabels 
            data={charts.psl_distribution} 
            colorMapping={{ 'PSL': CHART_COLORS.primary, 'Non-PSL': CHART_COLORS.neutral }}
          />
        </div>
      </ChartCard>

      {/* Chart 2: PSL by Sector (Bar) */}
      <ChartCard title="PSL by Sector" subtitle="Priority sector distribution">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.psl_by_sector}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" angle={-15} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/10000000).toFixed(0)}Cr`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Chart 3: Gender-wise Lending (Donut) */}
      <ChartCard title="Gender-wise Lending" subtitle="Borrower gender distribution">
        <div className="h-64">
          <DonutChartWithLabels 
            data={charts.gender_distribution} 
            colorMapping={{ 'Female': CHART_COLORS.tertiary, 'Male': CHART_COLORS.secondary }}
          />
        </div>
      </ChartCard>

      {/* Chart 4: PSL by Product Type (Stacked Bar) */}
      <ChartCard title="PSL by Product Type" subtitle="PSL vs Non-PSL by product">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.psl_by_product}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/10000000).toFixed(0)}Cr`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="PSL" stackId="a" fill={CHART_COLORS.primary} name="PSL" />
              <Bar dataKey="Non-PSL" stackId="a" fill={CHART_COLORS.neutral} name="Non-PSL" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </ChartGrid>
  );
};

export default PSLRegulatoryTab;
